import { useMemo } from 'react';
import intersection from 'lodash/intersection';
import { ALL_GUESTS } from 'common/components/TouchpointEditor/contexts/initialSessionContext';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { getDate, getTime } from '../utils/dateUtils';

export const useFilteredSessions = (
    sessions,
    tagsFilter,
    searchTermFilter,
    datesFilter,
    startTimesFilter,
    locationsFilter,
    eventListsFilter,
    registeredSessions,
) => {
    const { event } = useTouchpointData();
    const { shouldPreview } = usePreview({ event });

    return useMemo(() => {
        const filterByTags = (session) => !tagsFilter.length || intersection(tagsFilter, session.tags).length;
        const filterByDates = (session) => !datesFilter.length || datesFilter.includes(getDate(session.start));
        const filterByStartTimes = (session) =>
            !startTimesFilter.length || startTimesFilter.includes(getTime(session.start));
        const filterByLocations = (session) => !locationsFilter.length || locationsFilter.includes(session.location);
        const filterByText = (session) => {
            const lowerCasedSearchTermFilter = searchTermFilter.toLowerCase();
            return (
                session.name.toLowerCase().includes(lowerCasedSearchTermFilter) ||
                session.description.toLowerCase().includes(lowerCasedSearchTermFilter)
            );
        };
        const filterByEventLists = (session) => {
            if (!eventListsFilter.length || eventListsFilter.includes(ALL_GUESTS)) {
                return !session.eventLists || session.eventLists.length === 0;
            }
            const sessionEventLists = session.eventLists.map(({ id }) => String(id));
            return session.eventLists.length === 0 || !!intersection(eventListsFilter, sessionEventLists).length;
        };

        return sessions
            .filter(
                (session) =>
                    filterByTags(session) &&
                    filterByText(session) &&
                    filterByDates(session) &&
                    filterByStartTimes(session) &&
                    filterByLocations(session),
            )
            .filter((session) => (shouldPreview ? filterByEventLists(session) : true));
    }, [
        sessions,
        tagsFilter,
        searchTermFilter,
        datesFilter,
        startTimesFilter,
        locationsFilter,
        eventListsFilter,
        registeredSessions,
        shouldPreview,
    ]);
};
